import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  Ban,
  Link as LinkIcon,
  LoaderCircle,
  MailWarning,
  Pencil,
  Plus,
  Send,
  ShieldCheck,
  UserCheck,
  UserX,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { Modal } from "@/components/ui/modal";
import { useMe } from "@/lib/auth";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_USUARIOS, ESTADOS_INVITACION, type FiltrosUsuarios } from "@/lib/filtros/usuarios";
import { ROL_MIEMBRO_LABEL, ROL_MIEMBRO_OPCIONES } from "@/lib/obras";
import {
  type Usuario,
  useCorreoUsuarios,
  useCreateUsuario,
  useInvitar,
  useRevocarInvitacion,
  useToggleActivoUsuario,
  useUpdateUsuario,
  useUsuarios,
} from "@/lib/usuarios";
import { UsuarioDocumentoSection } from "./UsuarioDocumentoSection";
import { UsuarioForm, type UsuarioFormValues } from "./UsuarioForm";
import { UsuarioObrasSection } from "./UsuarioObrasSection";

// CHIPS_OBRA_VISIBLES son las obras que se listan en la columna antes de resumir el resto.
const CHIPS_OBRA_VISIBLES = 3;

// nombreCompleto compone el nombre visible de la persona ligada al usuario.
function nombreCompleto(u: Usuario): string {
  return `${u.persona.nombre} ${u.persona.apellidos}`.trim();
}

// limpio normaliza un texto opcional del formulario al payload del API (vacío ⇒ null).
function limpio(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

// toPersona proyecta los datos personales; persona.activo sigue al estado de la cuenta.
function toPersona(values: UsuarioFormValues) {
  return {
    nombre: values.persona.nombre.trim(),
    apellidos: values.persona.apellidos.trim(),
    dni_o_matricula: limpio(values.persona.dni_o_matricula),
    email: limpio(values.persona.email),
    cargo: limpio(values.persona.cargo),
    area: limpio(values.persona.area),
    activo: values.activo,
  };
}

// defaultsCrear son los valores iniciales del alta (cuenta operativa, no admin y activa).
// No hay contraseña: el alta INVITA (#239) y la clave la elige su titular.
const defaultsCrear: UsuarioFormValues = {
  persona: { nombre: "", apellidos: "", dni_o_matricula: "", email: "", cargo: "", area: "" },
  nombre_usuario: "",
  es_admin: false,
  activo: true,
};

// defaultsEditar mapea un usuario existente a los valores del formulario.
function defaultsEditar(u: Usuario): UsuarioFormValues {
  return {
    persona: {
      nombre: u.persona.nombre,
      apellidos: u.persona.apellidos,
      dni_o_matricula: u.persona.dni_o_matricula ?? "",
      email: u.persona.email ?? "",
      cargo: u.persona.cargo ?? "",
      area: u.persona.area ?? "",
    },
    nombre_usuario: u.nombre_usuario,
    es_admin: u.es_admin,
    activo: u.activo,
  };
}

// Etiquetas de la columna «Estado» para una cuenta que aún no ha aceptado su invitación
// (#239 D12/E1). El estado lo manda `invitacion_pendiente`; la invitación VIVA (o su
// ausencia) solo matiza el porqué, y de ahí sale la acción que se ofrece.
const ESTADO_INVITADO = "Invitado";
const ESTADO_INVITACION_CADUCADA = "Invitación caducada";
const ESTADO_INVITACION_REVOCADA = "Invitación revocada";

/**
 * estadoDe resuelve la etiqueta de estado de una fila. La invitación manda sobre el
 * activo/inactivo porque un invitado todavía no puede entrar; y revocar NO cierra el caso
 * (el usuario sigue pendiente), así que se distingue de una cuenta operativa: si no, la
 * fila caería a «Activo» mintiendo y perdería el «Reenviar» que la desbloquea (E1).
 */
function estadoDe(u: Usuario): string {
  if (!u.invitacion_pendiente) {
    return u.activo ? "Activo" : "Inactivo";
  }
  if (!u.invitacion) {
    return ESTADO_INVITACION_REVOCADA;
  }
  return u.invitacion.estado === "caducada" ? ESTADO_INVITACION_CADUCADA : ESTADO_INVITADO;
}

/**
 * UsuariosPage lista personas + cuentas de sistema con su estado (administrador
 * global / activo) y ofrece el alta/edición combinada (modal), el cambio rápido de
 * estado activo (PATCH) y la gestión de la invitación de registro (#239): dar de alta es
 * INVITAR, así que el alta no fija contraseña y la fila de un invitado ofrece reenviar o
 * revocar su enlace. Sin correo saliente configurado no se puede dar de alta a nadie.
 * F7 (#153): la cuenta es GLOBAL y sin rol; el rol y la autorización LOTO se gestionan
 * POR obra (ver la gestión de miembros de cada obra). Todo el slice es ADMIN-only: el
 * backend es el guardián real (RequireAdmin) y la UI se auto-oculta para quien no lo es.
 */
export function UsuariosPage() {
  const { data: me } = useMe();
  const esAdmin = me?.es_admin ?? false;

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_USUARIOS);
  // El rol es POR OBRA y el servidor responde 400 si llega sin ella, así que quitar la obra
  // quita también el rol: si no, la pantalla quedaría en un filtro imposible cuyo
  // desplegable culpable ni siquiera se pinta (solo aparece cuando hay obra elegida).
  const cambiarFiltros = useCallback(
    (nuevos: FiltrosUsuarios) => {
      setFiltros(nuevos.obra ? nuevos : { ...nuevos, rol: undefined });
    },
    [setFiltros],
  );
  const {
    data: usuarios,
    base: usuariosBase,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useUsuarios(filtros);
  const filas = usuarios ?? [];
  const crear = useCreateUsuario();
  const editar = useUpdateUsuario();
  const toggle = useToggleActivoUsuario();
  const invitar = useInvitar();
  const revocar = useRevocarInvitacion();
  // Sin correo saliente (SMTP + URL base) no se puede invitar y, por tanto, tampoco dar de
  // alta (#239 D10): la pantalla lo avisa antes de que el ADMIN rellene el formulario. El
  // botón nace deshabilitado (fail-closed: mientras no se confirme, no se ofrece un alta que
  // el servidor rechazaría), pero el AVISO solo se muestra cuando la consulta ha resuelto
  // que no está configurado (E8): en vuelo no se acusa de nada al despliegue.
  const { data: correo, isSuccess: correoResuelto } = useCorreoUsuarios();
  const puedeInvitar = correoResuelto && correo.configurado;
  const avisarSinCorreo = correoResuelto && !correo.configurado;

  const [formState, setFormState] = useState<
    { mode: "crear" } | { mode: "editar"; usuario: Usuario } | null
  >(null);

  const columns = useMemo<ColumnDef<Usuario, unknown>[]>(
    () => [
      {
        accessorKey: "nombre_usuario",
        header: "Usuario",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900">{row.original.nombre_usuario}</span>
        ),
      },
      {
        id: "nombre",
        accessorFn: (u) => nombreCompleto(u),
        header: "Nombre",
        cell: ({ getValue }) => <span className="text-slate-900">{getValue<string>()}</span>,
      },
      {
        id: "es_admin",
        accessorFn: (u) => (u.es_admin ? "Sí" : "No"),
        header: "Administrador",
        cell: ({ row }) =>
          row.original.es_admin ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 font-medium text-primary-800 text-sm">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Admin
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-600 text-sm">
              No
            </span>
          ),
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "microsoft",
        accessorFn: (u) => (u.entra_upn ? "Vinculado" : "No"),
        header: "Microsoft",
        cell: ({ row }) =>
          row.original.entra_upn ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 font-medium text-sky-800 text-sm"
              title={row.original.entra_upn}
            >
              <LinkIcon className="h-4 w-4" aria-hidden="true" />
              Vinculado
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-600 text-sm">
              No
            </span>
          ),
        meta: { className: "hidden lg:table-cell" },
      },
      // Columna de presentación pura (#346): tenía un `accessorFn` de texto plano que
      // alimentaba la búsqueda global, apagada aquí desde #247 (`sinBuscador`), y se quedó sin
      // ningún cliente —no ordena (`enableSorting: false`) y la `cell` pinta desde
      // `row.original.obras`—, así que se retiró. El filtro por obra y por rol es del SERVIDOR.
      {
        id: "obras",
        header: "Obras",
        enableSorting: false,
        cell: ({ row }) => {
          const obras = row.original.obras;
          if (obras.length === 0) {
            return <span className="text-slate-500">Sin obras</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {obras.slice(0, CHIPS_OBRA_VISIBLES).map((o) => (
                <span
                  key={o.id_obra}
                  className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700 text-sm"
                  title={o.nombre}
                >
                  {o.codigo_obra} · {ROL_MIEMBRO_LABEL[o.rol]}
                </span>
              ))}
              {obras.length > CHIPS_OBRA_VISIBLES ? (
                <span
                  className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-600 text-sm"
                  title={obras
                    .slice(CHIPS_OBRA_VISIBLES)
                    .map((o) => o.codigo_obra)
                    .join(", ")}
                >
                  +{obras.length - CHIPS_OBRA_VISIBLES}
                </span>
              ) : null}
            </div>
          );
        },
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "estado",
        accessorFn: (u) => estadoDe(u),
        header: "Estado",
        cell: ({ row }) => {
          const u = row.original;
          if (u.invitacion_pendiente) {
            // Un invitado no ha entrado nunca: su estado real es la invitación, no el
            // activo/inactivo de la cuenta.
            const estado = estadoDe(u);
            const pendiente = estado === ESTADO_INVITADO;
            return (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium text-sm ${
                  pendiente ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"
                }`}
                title={
                  u.invitacion
                    ? `Invitación enviada el ${new Date(u.invitacion.enviada_en).toLocaleDateString()}`
                    : "Sin invitación viva: reenvíala para que pueda entrar"
                }
              >
                {pendiente ? (
                  <Send className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <MailWarning className="h-4 w-4" aria-hidden="true" />
                )}
                {estado}
              </span>
            );
          }
          return (
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 font-medium text-sm ${
                u.activo ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
              }`}
            >
              {u.activo ? "Activo" : "Inactivo"}
            </span>
          );
        },
      },
      {
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => {
          const u = row.original;
          const nombre = nombreCompleto(u);
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Editar ${nombre}`}
                onClick={() => setFormState({ mode: "editar", usuario: u })}
              >
                <Pencil className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={u.activo ? `Desactivar ${nombre}` : `Activar ${nombre}`}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ id: u.id, activo: !u.activo })}
              >
                {u.activo ? (
                  <UserX className="h-5 w-5 text-red-600" aria-hidden="true" />
                ) : (
                  <UserCheck className="h-5 w-5 text-green-600" aria-hidden="true" />
                )}
              </Button>
              {/* Reenviar mientras el usuario siga pendiente —también tras revocar, que es
                  justo cuando hace falta— ; revocar solo si queda algo vivo que revocar (E1). */}
              {u.invitacion_pendiente ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Reenviar invitación a ${nombre}`}
                  disabled={invitar.isPending || !puedeInvitar}
                  onClick={() => void invitar.mutateAsync(u.id).catch(() => {})}
                >
                  <Send className="h-5 w-5 text-sky-700" aria-hidden="true" />
                </Button>
              ) : null}
              {u.invitacion_pendiente && u.invitacion ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Revocar invitación de ${nombre}`}
                  disabled={revocar.isPending}
                  onClick={() => void revocar.mutateAsync(u.id).catch(() => {})}
                >
                  <Ban className="h-5 w-5 text-red-600" aria-hidden="true" />
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [toggle, invitar, revocar, puedeInvitar],
  );

  // Campos de la barra de filtros. Los valores son los del API (ids, códigos de rol y los
  // estados de invitación), no las etiquetas. Las obras salen de las presentes en la lista
  // base; el rol EXIGE obra (el servidor responde 400 si falta), así que solo se ofrece
  // cuando ya hay una obra elegida.
  const opcionesObra = useMemo(() => {
    const vistas = new Map<number, string>();
    for (const u of usuariosBase ?? []) {
      for (const o of u.obras) {
        vistas.set(o.id_obra, `${o.codigo_obra} · ${o.nombre}`);
      }
    }
    return [...vistas.entries()]
      .map(([id, etiqueta]) => ({ valor: String(id), etiqueta }))
      .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta));
  }, [usuariosBase]);

  const campos = useMemo<CampoFiltro[]>(() => {
    const base: CampoFiltro[] = [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar usuarios…" },
      { tipo: "select", nombre: "obra", etiqueta: "Obra", opciones: opcionesObra },
    ];
    if (filtros.obra) {
      base.push({
        tipo: "select",
        nombre: "rol",
        etiqueta: "Rol en la obra",
        opciones: ROL_MIEMBRO_OPCIONES.map((r) => ({ valor: r.value, etiqueta: r.label })),
      });
    }
    base.push(
      { tipo: "booleano", nombre: "activo", etiqueta: "Activo" },
      // El flag GLOBAL de superadministrador (#285). Es la columna «Admin» de la tabla, y
      // desde #238 esta pantalla es el único sitio donde se reparte ese permiso.
      { tipo: "booleano", nombre: "es_admin", etiqueta: "Admin" },
      {
        tipo: "select",
        nombre: "invitacion",
        etiqueta: "Invitación",
        opciones: ESTADOS_INVITACION.map((e) => ({ valor: e.valor, etiqueta: e.etiqueta })),
      },
      {
        tipo: "booleano",
        nombre: "sso",
        etiqueta: "Microsoft",
        etiquetaSi: "Vinculado",
        etiquetaNo: "Sin vincular",
      },
    );
    return base;
  }, [opcionesObra, filtros.obra]);

  // El usuario en edición se relee del listado: tras guardar una pertenencia, la sección de
  // obras del modal debe reflejar el estado fresco del servidor (la instantánea del estado
  // se conserva como respaldo mientras el listado se refresca).
  const usuarioEnForm =
    formState?.mode === "editar"
      ? (usuarios?.find((u) => u.id === formState.usuario.id) ?? formState.usuario)
      : null;

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: UsuarioFormValues) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({
        id: formState.usuario.id,
        input: {
          persona: toPersona(values),
          nombre_usuario: values.nombre_usuario.trim(),
          es_admin: values.es_admin,
          activo: values.activo,
        },
      });
      cerrarForm();
      return;
    }
    // Alta: el modal NO se cierra, pasa a editar al usuario recién creado para que el ADMIN
    // le asigne sus obras sin volver a buscarlo en el listado (#238 D7).
    const creado = await crear.mutateAsync({
      persona: toPersona(values),
      nombre_usuario: values.nombre_usuario.trim(),
      es_admin: values.es_admin,
      activo: values.activo,
    });
    setFormState({ mode: "editar", usuario: creado });
  }

  // El backend es el guardián real (RequireAdmin); la UI se auto-oculta para no
  // ofrecer una sección vetada. El item de navegación también se oculta en AppLayout.
  if (me && !esAdmin) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>No tienes permiso para gestionar usuarios.</span>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Usuarios</h1>
          <p className="text-base text-slate-500">
            Personas y cuentas de acceso. El rol y la autorización LOTO se gestionan por obra.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {/* La exportación de usuarios es un informe GLOBAL de admin (dos hojas: identidad
              global + membresías de todas las obras): se sirve fuera del scope de obra en
              GET /api/v1/export/usuarios.xlsx (#180), así que el botón no necesita obra. */}
          <ExportButton tipo="usuarios" filtros={filtros} />
          <Button onClick={() => setFormState({ mode: "crear" })} disabled={!puedeInvitar}>
            <Plus className="h-5 w-5" aria-hidden="true" />
            Nuevo usuario
          </Button>
        </div>
      </div>

      {avisarSinCorreo ? (
        <Alert variant="destructive">
          <MailWarning className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            Sin correo saliente configurado no se pueden dar altas: el usuario nuevo recibe su
            invitación por correo. Revisa <code>LOTO_SMTP_*</code> y <code>LOTO_BASE_URL</code> en
            el servidor.
          </span>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar los usuarios. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando usuarios…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay usuarios. Crea el primero con «Nuevo usuario».
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={cambiarFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={(usuario) => setFormState({ mode: "editar", usuario })}
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar usuario" : "Nuevo usuario"}
        className="max-w-2xl"
      >
        {formState !== null ? (
          // La ficha (datos + cuenta + obras) puede pasar del alto de la pantalla: el cuerpo
          // del modal desplaza en vertical para que la sección de obras siga alcanzable.
          <div className="flex max-h-[70dvh] flex-col gap-5 overflow-y-auto pr-1">
            <UsuarioForm
              // La clave fuerza a remontar el formulario al pasar de «crear» a «editar»
              // (react-hook-form solo aplica defaultValues al montar).
              key={usuarioEnForm?.id ?? "nuevo"}
              mode={formState.mode}
              defaultValues={usuarioEnForm ? defaultsEditar(usuarioEnForm) : defaultsCrear}
              submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear usuario"}
              pending={crear.isPending || editar.isPending}
              onSubmit={onSubmitForm}
              onCancel={cerrarForm}
            />
            {/* La sección «Obras y rol» se guarda fila a fila contra sus propios endpoints,
                así que vive FUERA del <form> del usuario (anidar formularios es HTML
                inválido y su Enter dispararía el guardado de la ficha). */}
            {usuarioEnForm ? (
              <div className="border-slate-200 border-t pt-5">
                <UsuarioObrasSection usuario={usuarioEnForm} />
              </div>
            ) : null}
            {/* Documentación acreditativa (#435): también fuera del <form>, con sus propias
                rutas; el estado se refresca con el listado tras cada acción. */}
            {usuarioEnForm ? (
              <div className="border-slate-200 border-t pt-5">
                <UsuarioDocumentoSection usuario={usuarioEnForm} />
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
